import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, Clock, TrendingDown, AlertCircle, ChevronRight, Star, Zap, RefreshCw } from 'lucide-react';
import { rebatesApi } from '../services/rebatesApi';

const RebatesPage = ({ onNavigate }) => {
  const [selectedRebate, setSelectedRebate] = useState(null);
  const [rebates, setRebates] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, upcoming: 0, maxSavings: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch rebates from API
  const loadRebates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await rebatesApi.getAll();
      setRebates(data.rebates || []);
      setStats(data.stats || { total: 0, active: 0, upcoming: 0, maxSavings: 0 });
    } catch (err) {
      console.error('Error loading rebates:', err);
      setError('Failed to load rebates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRebates();
  }, []);

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'active-status';
      case 'upcoming': return 'upcoming-status';
      case 'expired': return 'expired-status';
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Group rebates by status
  const activeRebates = rebates.filter(r => r.status === 'active');
  const upcomingRebates = rebates.filter(r => r.status === 'upcoming');
  const featuredRebates = rebates.filter(r => r.featured && r.status !== 'expired');

  if (loading) {
    return (
      <div className="rebates-page">
        <div className="page-header rebates-header">
          <DollarSign size={40} />
          <h1>Rebates & Incentives Schedule</h1>
          <p>Loading current offers...</p>
        </div>
        <div className="loading-container">
          <RefreshCw size={48} className="spin" />
          <p>Loading rebates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rebates-page">
        <div className="page-header rebates-header">
          <DollarSign size={40} />
          <h1>Rebates & Incentives Schedule</h1>
        </div>
        <div className="error-container">
          <AlertCircle size={48} />
          <p>{error}</p>
          <button onClick={loadRebates} className="retry-btn">
            <RefreshCw size={16} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  if (rebates.length === 0) {
    return (
      <div className="rebates-page">
        <div className="page-header rebates-header">
          <DollarSign size={40} />
          <h1>Rebates & Incentives Schedule</h1>
          <p>Current and upcoming manufacturer rebates and dealer incentives</p>
        </div>
        <div className="empty-state-container">
          <DollarSign size={64} />
          <h2>No Rebates Available</h2>
          <p>Check back soon for upcoming promotions and incentives!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rebates-page">
      <div className="page-header rebates-header">
        <DollarSign size={40} />
        <h1>Rebates & Incentives Schedule</h1>
        <p>Current and upcoming manufacturer rebates and dealer incentives</p>
      </div>

      {/* Quick Stats */}
      <div className="rebates-stats">
        <div className="stat-card">
          <TrendingDown size={24} className="stat-icon" />
          <div>
            <h3>{stats.active}</h3>
            <p>Active Rebates</p>
          </div>
        </div>
        <div className="stat-card">
          <Clock size={24} className="stat-icon" />
          <div>
            <h3>{stats.upcoming}</h3>
            <p>Upcoming</p>
          </div>
        </div>
        <div className="stat-card">
          <DollarSign size={24} className="stat-icon" />
          <div>
            <h3>${(stats.maxSavings || 0).toLocaleString()}</h3>
            <p>Max Savings</p>
          </div>
        </div>
        <div className="stat-card">
          <Calendar size={24} className="stat-icon" />
          <div>
            <h3>{stats.total}</h3>
            <p>Total Offers</p>
          </div>
        </div>
      </div>

      {/* Featured Rebates */}
      {featuredRebates.length > 0 && (
        <div className="featured-rebates">
          <h2 className="section-title">
            <Zap size={20} />
            Featured Offers
          </h2>
          <div className="featured-grid">
            {featuredRebates.map(rebate => (
              <div key={rebate.id} className="featured-rebate-card">
                {rebate.status === 'active' && (
                  <div className="days-remaining">
                    {getDaysRemaining(rebate.endDate)} days left
                  </div>
                )}
                <div className="rebate-brand">{rebate.brand}</div>
                <h3>{rebate.title}</h3>
                <div className="rebate-amount">{rebate.amountText}</div>
                <div className="rebate-dates">
                  <Calendar size={14} />
                  {formatDate(rebate.startDate)} - {formatDate(rebate.endDate)}
                </div>
                <p className="rebate-description">{rebate.description}</p>
                <button 
                  className="view-details-btn"
                  onClick={() => setSelectedRebate(rebate)}
                >
                  View Details
                  <ChevronRight size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Rebates */}
      {activeRebates.length > 0 && (
        <div className="rebates-section">
          <h2 className="section-title">
            <Star size={20} />
            Active Rebates
          </h2>
          <div className="rebates-list">
            {activeRebates.map(rebate => (
              <div key={rebate.id} className="rebate-card">
                <div className={`status-indicator ${getStatusColor(rebate.status)}`}>
                  {rebate.status.toUpperCase()}
                </div>
                
                <div className="rebate-content">
                  <div className="rebate-header">
                    <div>
                      <h3>{rebate.title}</h3>
                      <div className="rebate-meta">
                        <span className="brand-tag">{rebate.brand}</span>
                        <span className="date-range">
                          <Calendar size={14} />
                          {formatDate(rebate.startDate)} - {formatDate(rebate.endDate)}
                        </span>
                        <span className="days-left">
                          <Clock size={14} />
                          {getDaysRemaining(rebate.endDate)} days remaining
                        </span>
                      </div>
                    </div>
                    <div className="rebate-amount-display">
                      {rebate.amountText}
                    </div>
                  </div>
                  
                  <p className="rebate-description">{rebate.description}</p>
                  
                  {rebate.models && rebate.models.length > 0 && (
                    <div className="rebate-models">
                      <strong>Applicable Models:</strong>
                      {rebate.models.map((model, idx) => (
                        <span key={idx} className="model-tag">{model}</span>
                      ))}
                    </div>
                  )}
                  
                  {rebate.terms && rebate.terms.length > 0 && (
                    <div className="rebate-terms">
                      <div className="terms-header" onClick={() => setSelectedRebate(rebate)}>
                        <AlertCircle size={14} />
                        Terms & Conditions
                        <ChevronRight size={14} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Rebates */}
      {upcomingRebates.length > 0 && (
        <div className="rebates-section">
          <h2 className="section-title">
            <Clock size={20} />
            Upcoming Rebates
          </h2>
          <div className="rebates-list">
            {upcomingRebates.map(rebate => (
              <div key={rebate.id} className="rebate-card upcoming">
                <div className={`status-indicator ${getStatusColor(rebate.status)}`}>
                  STARTS {formatDate(rebate.startDate).toUpperCase()}
                </div>
                
                <div className="rebate-content">
                  <div className="rebate-header">
                    <div>
                      <h3>{rebate.title}</h3>
                      <div className="rebate-meta">
                        <span className="brand-tag">{rebate.brand}</span>
                        <span className="date-range">
                          <Calendar size={14} />
                          {formatDate(rebate.startDate)} - {formatDate(rebate.endDate)}
                        </span>
                      </div>
                    </div>
                    <div className="rebate-amount-display">
                      {rebate.amountText}
                    </div>
                  </div>
                  
                  <p className="rebate-description">{rebate.description}</p>
                  
                  {rebate.models && rebate.models.length > 0 && (
                    <div className="rebate-models">
                      <strong>Applicable Models:</strong>
                      {rebate.models.map((model, idx) => (
                        <span key={idx} className="model-tag">{model}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rebate Details Modal */}
      {selectedRebate && (
        <div className="rebate-modal" onClick={() => setSelectedRebate(null)}>
          <div className="rebate-modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedRebate(null)}>
              ×
            </button>
            <h2>{selectedRebate.title}</h2>
            <div className="modal-amount">{selectedRebate.amountText}</div>
            
            <div className="modal-info">
              <div className="info-row">
                <strong>Brand:</strong> {selectedRebate.brand}
              </div>
              <div className="info-row">
                <strong>Valid Dates:</strong> {formatDate(selectedRebate.startDate)} - {formatDate(selectedRebate.endDate)}
              </div>
              <div className="info-row">
                <strong>Status:</strong> <span className={getStatusColor(selectedRebate.status)}>{selectedRebate.status.toUpperCase()}</span>
              </div>
              {selectedRebate.models && selectedRebate.models.length > 0 && (
                <div className="info-row">
                  <strong>Applicable Models:</strong>
                  <div className="models-list">
                    {selectedRebate.models.map((model, idx) => (
                      <span key={idx} className="model-tag">{model}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-description">
              <h4>Description</h4>
              <p>{selectedRebate.description}</p>
            </div>
            
            {selectedRebate.terms && selectedRebate.terms.length > 0 && (
              <div className="modal-terms">
                <h4>Terms & Conditions</h4>
                <ul>
                  {selectedRebate.terms.map((term, idx) => (
                    <li key={idx}>{term}</li>
                  ))}
                </ul>
              </div>
            )}
            
<div className="modal-actions">
                <button className="primary-action" onClick={() => { setSelectedRebate(null); onNavigate && onNavigate('contact'); }}>
                  Contact Dealer
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Footer CTA */}
      <div className="rebates-footer">
        <h3>Don't Miss Out on Savings!</h3>
        <p>Contact your local Centurion dealer to take advantage of these limited-time offers</p>
        <button className="cta-button" onClick={() => onNavigate && onNavigate('contact')}>
          Find a Dealer Near You
        </button>
      </div>
    </div>
  );
};

export default RebatesPage;
